import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minutes before logout

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
  extendSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const warningShownRef = useRef(false);

  const extendSession = useCallback(() => {
    setLastActivity(Date.now());
    warningShownRef.current = false;
  }, []);

  const handleSignOut = useCallback(async () => {
    warningShownRef.current = false;
    await supabase.auth.signOut();
  }, []);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      warningShownRef.current = false;
    };
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, updateActivity));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, []);

  // Auto logout after 30 minutes of inactivity with 5 minute warning
  useEffect(() => {
    if (!user) return;

    const checkTimeout = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;
      const timeUntilLogout = SESSION_TIMEOUT_MS - timeSinceLastActivity;
      
      // Show warning 5 minutes before logout
      if (timeUntilLogout <= WARNING_BEFORE_MS && timeUntilLogout > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        const minutesLeft = Math.ceil(timeUntilLogout / 60000);
        toast({
          title: "Session läuft ab",
          description: `Sie werden in ${minutesLeft} Minuten automatisch abgemeldet. Bewegen Sie die Maus oder drücken Sie eine Taste, um angemeldet zu bleiben.`,
          duration: 30000,
        });
      }
      
      // Logout after timeout
      if (timeSinceLastActivity >= SESSION_TIMEOUT_MS) {
        handleSignOut();
        toast({
          title: "Automatisch abgemeldet",
          description: "Sie wurden aus Sicherheitsgründen nach 30 Minuten Inaktivität abgemeldet.",
        });
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkTimeout);
  }, [user, lastActivity, handleSignOut]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session) {
          setLastActivity(Date.now());
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session) {
        setLastActivity(Date.now());
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = handleSignOut;

  return (
    <AuthContext.Provider value={{ user, session, signIn, signUp, signOut, loading, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
